import { getApiUrl, fetchBackendApi } from "../utils/apiUtils";
import { getAuthData } from "../utils/sessionStorage";
import { makeApiCall } from '../utils/nativeApiUtils';

// Get backend URL helper
const getBackendUrl = () => {
  return process.env.REACT_APP_MIRASOL_BACKEND_URL || '';
};

// Using unified API call function from utils
// makeApiCall is imported from '../utils/nativeApiUtils'

// Handle WebSocket connection
export const connectWebSocket = async (websocketRef, setIsConnected, setTranscript, setSpeakerSegments, setSoapNote) => {
  if (websocketRef.current) {
    websocketRef.current.close();
  }

  try {
    // Use fetchBackendApi for consistent API calls
    const response = await fetchBackendApi('/live-transcription/');
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    const wsUrl = data.websocket_url;

    try {
      // Create WebSocket connection with explicit protocol
      const ws = new WebSocket(wsUrl);

      // Set a connection timeout
      const connectionTimeout = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          ws.close();
          alert('Failed to connect to the WebSocket server. Please check your connection and try again.');
        }
      }, 5000);

      ws.onopen = () => {
        setIsConnected(true);
        clearTimeout(connectionTimeout);

        // Start a new session
        const startMessage = JSON.stringify({
          type: 'start_session'
        });
        ws.send(startMessage);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'transcription_update') {
            setTranscript(data.transcript);
            setSpeakerSegments(data.speaker_segments);
          } else if (data.type === 'soap_note_update') {
            setSoapNote(data.soap_note);
          } else if (data.type === 'error') {
            console.error('WebSocket error from server:', data.message);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onclose = (event) => {
        setIsConnected(false);
        clearTimeout(connectionTimeout);
      };

      ws.onerror = (error) => {
        console.error('WebSocket error event:', error);
      };

      websocketRef.current = ws;
    } catch (error) {
      alert('Failed to establish WebSocket connection. Please try again later.');
    }
  } catch (error) {
    alert(`Failed to get WebSocket URL: ${error.message}. Please try again later.`);
  }
};

// Handle audio file upload
export const handleAudioUpload = async (audioFile) => {
  try {
    // Get API URL using the utility function
    const url = getApiUrl('/transcribe-audio/');
    const formData = new FormData();
    formData.append('audio', audioFile);

    const response = await makeApiCall(url, {
      method: 'POST',
      body: formData,
      mode: 'cors',
      headers: {
        'X-Sol-API-Key': process.env.REACT_APP_SOL_API_KEY
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();

    // Return the transcription and Visit note data
    return {
      transcript: data.transcript || '',
      soap_note: data.soap_note || '',
      speaker_segments: data.speaker_segments || []
    };
  } catch (error) {
    alert(`Error uploading audio: ${error.message}. Please try again.`);
    throw error; // Re-throw to let the component handle it
  }
};

// ========================================
// TEMPLATE ENDPOINTS (Bearer Token Auth)
// ========================================

// Provider Templates (Full CRUD with Bearer token)
export const fetchProviderTemplates = async (apiToken, providerId, params = {}) => {
  try {
    const backendUrl = getBackendUrl();
    const queryParams = new URLSearchParams({ provider_id: providerId, ...params });
    const url = `${backendUrl}/api/provider/note/templates/?${queryParams.toString()}`;
    
    const response = await makeApiCall(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      }
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    
    return data;
  } catch (error) {
    throw error;
  }
};

export const getProviderTemplate = async (apiToken, templateId) => {
  try {
    const backendUrl = getBackendUrl();
    const response = await makeApiCall(`${backendUrl}/api/provider/note/templates/${templateId}/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      }
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    
    return data;
  } catch (error) {
    throw error;
  }
};

export const createProviderTemplate = async (apiToken, providerId, template) => {
  try {
    const backendUrl = getBackendUrl();
    const response = await makeApiCall(`${backendUrl}/api/provider/note/templates/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      },
      body: JSON.stringify({
        ...template,
        provider_id: providerId
      })
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    
    return data;
  } catch (error) {
    throw error;
  }
};

export const updateProviderTemplate = async (apiToken, providerId, templateId, updates) => {
  try {
    const backendUrl = getBackendUrl();
    const response = await makeApiCall(`${backendUrl}/api/provider/note/templates/${templateId}/`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      },
      body: JSON.stringify({
        ...updates,
        provider_id: providerId
      })
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    
    return data;
  } catch (error) {
    throw error;
  }
};

export const deleteProviderTemplate = async (apiToken, providerId, templateId) => {
  try {
    const backendUrl = getBackendUrl();
    const response = await makeApiCall(`${backendUrl}/api/provider/note/templates/${templateId}/`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      },
      body: JSON.stringify({ provider_id: providerId })
    });
    
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    
    return { success: true };
  } catch (error) {
    throw error;
  }
};

// Healthcare System Templates (Read-only with Bearer token)
export const fetchHealthSystemTemplates = async (apiToken, params = {}) => {
  try {
    const backendUrl = getBackendUrl();
    const queryString = new URLSearchParams(params).toString();
    const url = `${backendUrl}/api/health-system/note/templates/${queryString ? '?' + queryString : ''}`;
    
    const response = await makeApiCall(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      }
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    
    return data;
  } catch (error) {
    throw error;
  }
};

export const getHealthSystemTemplate = async (apiToken, templateId) => {
  try {
    const backendUrl = getBackendUrl();
    const response = await makeApiCall(`${backendUrl}/api/health-system/note/templates/${templateId}/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      }
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    
    return data;
  } catch (error) {
    throw error;
  }
};

// Global/System Templates (Read-only with Bearer token)
export const fetchGlobalTemplates = async (apiToken, params = {}) => {
  try {
    const backendUrl = getBackendUrl();
    const queryString = new URLSearchParams(params).toString();
    const url = `${backendUrl}/api/system/note/templates/${queryString ? '?' + queryString : ''}`;
    
    const response = await makeApiCall(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      }
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    
    return data;
  } catch (error) {
    throw error;
  }
};

// Trigger visit note generation with comprehensive settings
export const triggerNoteGeneration = async (apiToken, payload) => {
  try {
    const backendUrl = getBackendUrl();
    const url = `${backendUrl}/api/provider/audio/note/generate/`;

    const response = await makeApiCall(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();
    
    if (!response.ok) {
      // Return error details for proper handling in the component
      return {
        success: false,
        error: data.error || 'Failed to generate note',
        detail: data.detail || '',
        status: response.status
      };
    }
    return {
      success: true,
      ...data
    };
  } catch (error) {
    throw error;
  }
};

// Trigger content evaluation with note templates using dedicated quanta endpoint
export const triggerContentEvaluation = async (apiToken, payload) => {
  try {
    const backendUrl = getBackendUrl();
    const url = `${backendUrl}/api/provider/quanta/evaluate/`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();
    
    if (!response.ok) {
      // Return error details for proper handling in the component
      return {
        success: false,
        error: data.error || 'Failed to evaluate content',
        detail: data.detail || '',
        status: response.status
      };
    }
    return {
      success: true,
      ...data
    };
  } catch (error) {
    // Return error in consistent format
    return {
      success: false,
      error: error.message || 'Network error occurred',
      detail: 'Failed to connect to the server',
      status: 0
    };
  }
};

// Submit provider feedback
export const submitProviderFeedback = async (feedbackData) => {
  try {
    const url = getApiUrl('/provider/notes/feedback/');

    const response = await makeApiCall(url, {
      method: 'POST',
      headers: {
        'X-Sol-API-Key': process.env.REACT_APP_SOL_API_KEY,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(feedbackData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    return data; // Assuming a success message or similar
  } catch (error) {
    throw error;
  }
};

// Update provider settings
export const updateProviderSettings = async (settingsData) => {
  try {
    const url = getApiUrl('/provider/notes/settings/');

    const response = await makeApiCall(url, {
      method: 'PUT',
      headers: {
        'X-Sol-API-Key': process.env.REACT_APP_SOL_API_KEY,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(settingsData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    return data; // Assuming a success message or similar
  } catch (error) {
    throw error;
  }
};

// Bearer token authenticated API calls (for admin endpoints)
// Validate token with backend
export const validateToken = async (apiToken) => {
  try {
    const backendUrl = getBackendUrl();
    
    const response = await makeApiCall(`${backendUrl}/api/admin/token/system/verify/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      },
      body: JSON.stringify({
        api_token: apiToken
      })
    });

    const data = await response.json();
    return { response, data };
  } catch (error) {
    throw new Error(`Network error: ${error.message}`);
  }
};
// Fetch provider details
export const fetchProviderDetails = async (providerId, apiToken) => {
  try {
    const backendUrl = getBackendUrl();
    
    const response = await makeApiCall(`${backendUrl}/api/admin/providers/${providerId}/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      }
    });
    const data = await response.json();    
    if (response.ok && data.success) {
      return data.provider;
    } else {
      throw new Error(data.error || 'Failed to fetch provider details');
    }
  } catch (error) {
    throw new Error(`Provider fetch error: ${error.message}`);
  }
};

// Fetch provider cases with external token authentication
export const fetchProviderCases = async (providerId, apiToken, page = 1, pageSize = 20, search = '') => {
  try {
    const backendUrl = getBackendUrl();    
    const queryParams = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString()
    });    
    if (search && search.trim()) {
      queryParams.append('search', search.trim());
    }    
    const response = await makeApiCall(`${backendUrl}/api/admin/providers/${providerId}/cases/?${queryParams}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      }
    });
    const data = await response.json();    
    if (response.ok && data.success) {
      return {
        cases: data.data,
        pagination: data.pagination
      };
    } else {
      throw new Error(data.error || 'Failed to fetch provider cases');
    }
  } catch (error) {
    throw error;
  }
};

// Fetch all cases for the provider's health system
export const fetchHealthSystemCases = async (apiToken, page = 1, pageSize = 20, search = '', filters = {}) => {
  try {
    const backendUrl = getBackendUrl();    
    const queryParams = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString()
    });    
    
    if (search && search.trim()) {
      queryParams.append('search', search.trim());
    }
    
    // Add optional filters
    if (filters.status && filters.status.trim()) {
      queryParams.append('status', filters.status.trim());
    }
    if (filters.provider_id && filters.provider_id.trim()) {
      queryParams.append('provider_id', filters.provider_id.trim());
    }
    if (filters.dateFrom && filters.dateFrom.trim()) {
      queryParams.append('date_from', filters.dateFrom.trim());
    }
    if (filters.dateTo && filters.dateTo.trim()) {
      queryParams.append('date_to', filters.dateTo.trim());
    }
    if (filters.scope && filters.scope.trim()) {
      queryParams.append('scope', filters.scope.trim());
    }
    
    const response = await makeApiCall(`${backendUrl}/api/healthcare/cases/?${queryParams}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      }
    });
    const data = await response.json();    
    if (response.ok && data.success) {
      return {
        cases: data.data,
        pagination: data.pagination
      };
    } else {
      throw new Error(data.error || 'Failed to fetch health system cases');
    }
  } catch (error) {
    throw error;
  }
};

// Fetch Zoom session token
export const fetchZoomToken = async (payload) => {
  try {
    const response = await fetchBackendApi('/zoom/session/token/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to parse error response' }));
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorData.error || response.statusText}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

// Visit Note operations with Bearer token
export const createVisitNote = async (apiToken, sessionId, noteData) => {
  try {
    const backendUrl = getBackendUrl();
    const response = await makeApiCall(`${backendUrl}/api/provider/note/${sessionId}/create/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      },
      body: JSON.stringify(noteData)
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    
    return data;
  } catch (error) {
    throw error;
  }
};

export const fetchVisitNotes = async (apiToken, sessionId) => {
  try {
    const backendUrl = getBackendUrl();
    const response = await makeApiCall(`${backendUrl}/api/provider/note/${sessionId}/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      }
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    
    return data;
  } catch (error) {
    throw error;
  }
};

export const submitNoteFeedback = async (apiToken, noteId, feedback) => {
  try {
    const backendUrl = getBackendUrl();
    const response = await makeApiCall(`${backendUrl}/api/provider/note/${noteId}/feedback/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      },
      body: JSON.stringify(feedback)
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    
    return data;
  } catch (error) {
    throw error;
  }
};

// Template copy helpers
export const copyHealthcareTemplateToProvider = async (apiToken, providerId, templateId) => {
  try {
    // First fetch the healthcare template
    const template = await getHealthSystemTemplate(apiToken, templateId);
    
    // Create a copy as a provider template
    return await createProviderTemplate(apiToken, providerId, {
      name: `Copy of ${template.name}`,
      description: template.description,
      content: template.content,
      template_type: template.template_type,
      specialty: template.specialty,
      source_template_id: templateId,
      source_type: 'healthcare'
    });
  } catch (error) {
    throw error;
  }
};

export const copyGlobalTemplateToProvider = async (apiToken, providerId, template) => {
  try {
    // Create a copy as a provider template from global template data
    return await createProviderTemplate(apiToken, providerId, {
      name: `Copy of ${template.name}`,
      description: template.description,
      content: template.content,
      template_type: template.template_type,
      specialty: template.specialty,
      source_template_id: template.id,
      source_type: 'global'
    });
  } catch (error) {
    throw error;
  }
};

// Get list of providers in the same health system
export const getHealthSystemProviders = async (apiToken, providerId) => {
  try {
    const backendUrl = getBackendUrl();
    const queryParams = new URLSearchParams({ provider_id: providerId });
    const url = `${backendUrl}/api/healthcare/providers/?${queryParams.toString()}`;
    
    const response = await makeApiCall(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || errorData.detail || `HTTP ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

// Search other providers' templates within the same health system
export const searchOtherProviderTemplates = async (apiToken, providerId, searchProviderId) => {
  try {
    const backendUrl = getBackendUrl();
    const queryParams = new URLSearchParams({ 
      provider_id: providerId,
      search_provider_id: searchProviderId
    });
    const url = `${backendUrl}/api/provider/note/templates/?${queryParams.toString()}`;
    
    const response = await makeApiCall(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      }
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    
    return data;
  } catch (error) {
    throw error;
  }
};

// Copy another provider's template
export const copyProviderTemplateToProvider = async (apiToken, providerId, template) => {
  try {
    // Create a copy as a provider template from another provider's template
    return await createProviderTemplate(apiToken, providerId, {
      name: `Copy of ${template.name}`,
      description: template.description,
      content: template.content,
      template_type: template.template_type,
      specialty: template.specialty,
      source_template_id: template.id,
      source_type: 'provider'
    });
  } catch (error) {
    throw error;
  }
};

// ========================================
// CASE UPDATE ENDPOINTS (Bearer Token Auth)
// ========================================

// Update provider case status
export const updateProviderCaseStatus = async (apiToken, caseId, status, additionalData = {}) => {
  try {
    const backendUrl = getBackendUrl();
    const payload = {
      status,
      timestamp: new Date().toISOString(),
      provider_session_data: {
        provider_joined: status === 'provider_joined',
        session_started: status === 'session_started',
        session_ended: status === 'session_ended',
        ...additionalData
      }
    };

    const response = await makeApiCall(`${backendUrl}/api/provider/case/${caseId}/status/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      },
      body: JSON.stringify(requestData)
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    
    return data;
  } catch (error) {
    throw error;
  }
};

// Update video session progress
export const updateVideoSessionProgress = async (apiToken, caseId, sessionData) => {
  try {
    const backendUrl = getBackendUrl();
    const requestData = {
      session_progress: {
        transcription_started: sessionData.transcriptionStarted,
        recording_started: sessionData.recordingStarted,
        provider_notes_created: sessionData.notesCreated,
        session_duration: sessionData.duration,
        participants_count: sessionData.participantsCount,
        ...sessionData
      },
      timestamp: new Date().toISOString()
    };

    const response = await makeApiCall(`${backendUrl}/api/provider/case/${caseId}/session/progress/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      },
      body: JSON.stringify(requestData)
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    
    return data;
  } catch (error) {
    throw error;
  }
};

// Complete provider session
export const completeProviderSession = async (apiToken, caseId, sessionSummary) => {
  try {
    const backendUrl = getBackendUrl();
    const requestData = {
      session_completed: true,
      completion_timestamp: new Date().toISOString(),
      session_summary: {
        total_duration: sessionSummary.duration,
        transcription_generated: sessionSummary.transcriptionGenerated,
        notes_generated: sessionSummary.notesGenerated,
        provider_feedback_submitted: sessionSummary.feedbackSubmitted,
        recording_saved: sessionSummary.recordingSaved,
        ...sessionSummary
      }
    };

    const response = await makeApiCall(`${backendUrl}/api/provider/case/${caseId}/complete/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      },
      body: JSON.stringify(requestData)
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    
    return data;
  } catch (error) {
    throw error;
  }
};

// Get case status updates
export const getCaseStatus = async (apiToken, caseId) => {
  try {
    const backendUrl = getBackendUrl();
    const response = await makeApiCall(`${backendUrl}/api/provider/case/${caseId}/status/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      }
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    
    return data;
  } catch (error) {
    throw error;
  }
};

// ========================================
// UNIFIED AUDIO SESSION ENDPOINTS
// ========================================

// Get unified WebSocket URL for audio sessions
export const getUnifiedWebSocketUrl = async (sessionType, sessionData) => {
  try {
    const response = await fetchBackendApi('/audio/unified-websocket/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        session_type: sessionType, // 'zoom', 'twilio', 'in-person'
        session_data: sessionData
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    return data.websocket_url;
  } catch (error) {
    throw error;
  }
};

// Start unified audio session
export const startUnifiedAudioSession = async (apiToken, caseId, sessionType, sessionData) => {
  try {
    const backendUrl = getBackendUrl();
    const payload = {
      case_id: caseId,
      session_type: sessionType,
      session_data: sessionData,
      realtime_transcription: sessionData.realtimeTranscription || false,
      timestamp: new Date().toISOString()
    };

    const response = await makeApiCall(`${backendUrl}/api/audio/session/start/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      },
      body: JSON.stringify(payload)
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    
    return data;
  } catch (error) {
    throw error;
  }
};

// Update unified audio session
export const updateUnifiedAudioSession = async (apiToken, sessionId, updateData) => {
  try {
    const backendUrl = getBackendUrl();
    const payload = {
      session_id: sessionId,
      update_data: updateData,
      timestamp: new Date().toISOString()
    };

    const response = await makeApiCall(`${backendUrl}/api/audio/session/${sessionId}/update/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      },
      body: JSON.stringify(payload)
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    
    return data;
  } catch (error) {
    throw error;
  }
};

// Stop unified audio session
export const stopUnifiedAudioSession = async (apiToken, sessionId, metadata = {}) => {
  try {
    const backendUrl = getBackendUrl();
    const payload = {
      session_id: sessionId,
      ...metadata
    };

    const response = await makeApiCall(`${backendUrl}/api/provider/audio/session/stop/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      },
      body: JSON.stringify(payload)
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    
    return data;
  } catch (error) {
    // console.error('[API] Error stopping unified audio session:', error);
    throw error;
  }
};

// Upload audio segment to S3
export const uploadAudioSegment = async (audioBlob, sessionId, segmentIndex) => {
  try {
    const formData = new FormData();
    formData.append('audio', audioBlob, `segment_${segmentIndex}.mp3`);
    formData.append('session_id', sessionId);
    formData.append('segment_index', segmentIndex);

    const response = await fetchBackendApi('/audio/upload-segment/', {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

// Get audio session status (Updated to use correct endpoint)
export const getAudioSessionStatus = async (apiToken, sessionId) => {
  try {
    const backendUrl = getBackendUrl();
    const response = await makeApiCall(`${backendUrl}/api/provider/audio/session/${sessionId}/status/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      }
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    
    return data;
  } catch (error) {
    throw error;
  }
};

// List all audio sessions for a case
export const listCaseAudioSessions = async (apiToken, caseId) => {
  try {
    const backendUrl = getBackendUrl();
    const response = await makeApiCall(`${backendUrl}/api/healthcare/cases/${caseId}/audio-sessions/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      }
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    
    return data;
  } catch (error) {
    throw error;
  }
};

// Fetch transcripts for a case by getting all audio sessions and their segments
export const fetchCaseTranscripts = async (apiToken, caseId) => {
  try {
    // First get all audio sessions for the case
    const sessionsData = await listCaseAudioSessions(apiToken, caseId);
    const sessions = sessionsData.sessions || [];
    
    const transcripts = [];
    
    // For each session, get detailed status to fetch segments with transcripts
    for (const session of sessions) {
      try {
        const sessionStatus = await getAudioSessionStatus(apiToken, session.session_id);
        
        if (sessionStatus.segments && sessionStatus.segments.length > 0) {
          // Process segments to extract transcript data
          const sessionTranscript = {
            session_id: session.session_id,
            status: session.status,
            created_at: session.created_at,
            completed_at: session.completed_at,
            segments: sessionStatus.segments.map(segment => ({
              segment_number: segment.segment_number,
              source: segment.source,
              duration_seconds: segment.duration_seconds,
              transcription_status: segment.transcription_status,
              // Note: Individual segments no longer have transcript_text
              // The session-level transcript is now available separately
              s3_url: segment.s3_url || '',
              start_time: segment.start_time,
              end_time: segment.end_time
            })), // Show ALL segments, no filtering
            session_transcript: sessionStatus.session_transcript || null, // NEW: Session-level transcript
            visit_note_id: sessionStatus.visit_note_id || session.visit_note_id
          };
          
          if (sessionTranscript.segments.length > 0) {
            transcripts.push(sessionTranscript);
          }
        }
      } catch (error) {
      }
    }
    
    return {
      case_id: caseId,
      sessions_count: sessions.length,
      transcripts: transcripts.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      ) // Sort by most recent first
    };
    
  } catch (error) {
    throw error;
  }
};

// Trigger batch transcription processing
export const triggerBatchTranscription = async (apiToken, sessionId) => {
  try {
    const backendUrl = getBackendUrl();
    const response = await makeApiCall(`${backendUrl}/api/audio/session/${sessionId}/transcribe/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      }
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    
    return data;
  } catch (error) {
    throw error;
  }
};

// Fetch all specialties from backend
export const fetchSpecialties = async (apiToken) => {
  try {
    const backendUrl = getBackendUrl();
    
    const response = await makeApiCall(`${backendUrl}/api/provider/specialties/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      }
    });

    const data = await response.json();
    
    if (response.ok && data.success) {
      return data.specialties;
    } else {
      throw new Error(data.error || 'Failed to fetch specialties');
    }
  } catch (error) {
    throw new Error(`Specialties fetch error: ${error.message}`);
  }
};

// Fetch case details (provider endpoint)
export const fetchCaseDetails = async (apiToken, caseId, providerId = null) => {
  try {
    // Get provider ID from localStorage if not provided
    let actualProviderId = providerId;
    if (!actualProviderId) {
      const authData = getAuthData();
      if (authData && authData.decoded_jwt && authData.decoded_jwt.provider_id) {
        actualProviderId = authData.decoded_jwt.provider_id;
      } else {
        throw new Error('Provider ID not found in auth data');
      }
    }
    
    const response = await fetchBackendApi(`/api/provider/${actualProviderId}/cases/${caseId}/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      }
    });

    // Check if response is JSON before parsing
    const contentType = response.headers.get('content-type');
    let data;

    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      // Handle non-JSON responses (likely HTML error pages)
      const textResponse = await response.text();

      if (response.status === 404) {
        throw new Error('Case not found');
      } else if (response.status === 403) {
        throw new Error('Access denied - insufficient permissions');
      } else if (response.status >= 500) {
        throw new Error('Server error - please try again later');
      } else {
        throw new Error(`Unexpected response (${response.status}): Server returned non-JSON data`);
      }
    }

    if (response.ok) {
      return data;
    } else {
      throw new Error(data.error || data.detail || 'Failed to fetch case details');
    }
  } catch (error) {
    throw new Error(`${error.message}`);
  }
};

// Fetch all visit notes for a case (healthcare endpoint)
export const fetchCaseVisitNotes = async (apiToken, caseId) => {
  try {
    const backendUrl = getBackendUrl();
    const response = await makeApiCall(`${backendUrl}/api/healthcare/cases/${caseId}/notes/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      }
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    
    return data;
  } catch (error) {
    throw error;
  }
};

// Convert visit note to different formats (HTML, Plain Text, RTF)
export const convertNoteFormat = async (apiToken, noteId, format) => {
  try {
    const backendUrl = getBackendUrl();
    const response = await makeApiCall(`${backendUrl}/api/note/convert/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      },
      body: JSON.stringify({
        note_id: noteId,
        content_type: format  // Backend expects 'content_type' not 'format'
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }

    return data;
  } catch (error) {
    throw error;
  }
};

// Trigger transcription for audio session or segment
export const triggerTranscription = async (apiToken, sessionId, segmentId = null) => {
  try {
    const backendUrl = getBackendUrl();
    const url = `${backendUrl}/api/provider/audio/transcription/trigger/`;
    
    const body = {
      session_id: sessionId
    };
    
    if (segmentId) {
      body.segment_id = segmentId;
    }
    
    const response = await makeApiCall(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      return { 
        success: false, 
        error: errorData.error || `HTTP error! Status: ${response.status}` 
      };
    }
    
    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    return { 
      success: false, 
      error: error.message || 'Failed to trigger transcription' 
    };
  }
};

// Fetch visit note by ID
export const fetchVisitNoteById = async (apiToken, noteId) => {
  try {
    const backendUrl = getBackendUrl();
    const response = await makeApiCall(`${backendUrl}/api/provider/note/${noteId}/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }

    return data;
  } catch (error) {
    throw error;
  }
};

// Update visit note content
export const updateVisitNote = async (apiToken, visitNoteId, content, feedback = '') => {
  try {
    const backendUrl = getBackendUrl();
    const response = await makeApiCall(`${backendUrl}/api/provider/note/${visitNoteId}/update/`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      },
      body: JSON.stringify({
        generated_content: content,
        feedback: feedback
      })
    });
    const data = await response.json();    
    if (!response.ok) {
      throw new Error(data.error || data.detail || 'Failed to update visit note');
    }
    return data;
  } catch (error) {
    throw error;
  }
};
